import { useEffect, useState } from "react"
import { captureMessage } from "@sentry/react"
import parse from "html-react-parser"
import classnames from "classnames"

import PurchaseModal from "app/components/subscriptions/purchase_modal"
import Modal from "shared/components/modal"
import closeX from "images/components/x.svg?raw"
import { loadState, saveState } from "shared/localstorage_helpers"
import moment from "moment"
import paidBenefits from "app/components/subscriptions/shared/paid_benefits"

import styles from "./index.module.scss"

interface URLs {
  modalSubscriptionUrl: string
  newProSubscriptionUrl: string
  newProPlusSubscriptionUrl: string
}

interface SubscriptionProps {
  assets: {
    iftttSvgUrl: string
    iftttWhieSvgUrl: string
    proBadgeSvgUrl: string
    proBadgeWhiteSvgUrl: string
    proPlusBadgeSvgUrl: string
    proPlusBadgeWhiteSvgUrl: string
  }
  browser_type: string
  create_stripe_customer_url: string
  eligible_for_trial: boolean
  email: string
  initial_promo_code: string | null
  intermediate_pro: { product_id: string; plan_id: string; tier: string; monthly_plan: object; yearly_plan: object }
  pro: { product_id: string; plan_id: string; tier: string; monthly_plan: object; yearly_plan: object }
  subscriptions_path: string
}

interface Props {
  initialPromoCode: string
  modalCopyObj: {
    checkoutModalTarget: "intermediate_pro" | "pro"
    eligibleForTrialCopy: string
    copy: string
    title: string
  }
  onClose: () => void
  onUpgrade: (a: string) => void
  step: string
  urls: URLs
  visible: boolean
}

const CheckoutModal = ({ modalCopyObj, onClose, onUpgrade, step, urls, visible, initialPromoCode }: Props) => {
  const [subscriptionProps, updateSubscriptionProps]: [SubscriptionProps, (a: SubscriptionProps) => void] =
    useState<SubscriptionProps>({} as SubscriptionProps)
  const [subscriptionPropsLoaded, setSubscriptionPropsLoaded]: [boolean, (a: boolean) => void] = useState(false)
  const { checkoutModalTarget, eligibleForTrialCopy, title } = modalCopyObj

  const checkoutStorageId = `checkoutModalSubscriptionProps-${window.App.user?.id}-v3`
  const loadCachedSubscriptionProps = () => JSON.parse(loadState(checkoutStorageId) || "null")
  const cacheSubscriptionProps = data => {
    saveState(
      JSON.stringify({
        ttl: moment().add(10, "minutes").unix(),
        data,
      }),
      checkoutStorageId
    )
  }

  const loadSubscriptionProps = () => {
    const cachedSubscriptionProps = loadCachedSubscriptionProps()

    if (cachedSubscriptionProps && moment().unix() < cachedSubscriptionProps.ttl) {
      if (window.App.env && window.App.allowDebugErrorMessages) {
        console.log("subscriptionProps loaded from cache", cachedSubscriptionProps)
      }
      updateSubscriptionProps(cachedSubscriptionProps.data)
      setSubscriptionPropsLoaded(true)
      return
    }

    fetch(urls.modalSubscriptionUrl, {
      method: "get",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      redirect: "error",
    })
      .then(response => {
        if (!response.ok) {
          throw new Error("HTTP error " + response.status)
        }

        return response.json()
      })
      .then(result => {
        updateSubscriptionProps(result)
        setSubscriptionPropsLoaded(true)
        cacheSubscriptionProps(result)
      })
      .catch(() => {
        const modalTargetPlan = modalCopyObj.checkoutModalTarget
        // In case that we have an issue load data to be used in SignUpAndPurchase, not block user. Redirect them to checkout page.
        captureMessage("Checkout modal failed to load subscription data and redirect user to checkout page.", {
          tags: { user_id: window.App.user?.id },
          extra: { modalTargetPlan },
        })
        window.location.assign(
          modalCopyObj.checkoutModalTarget === "intermediate_pro"
            ? urls.newProSubscriptionUrl
            : urls.newProPlusSubscriptionUrl
        )
      })
  }

  useEffect(() => {
    loadSubscriptionProps()
  }, [])

  useEffect(() => {
    if (visible) {
      window.App.Utils?.logCustomDatadogAction("stripe_checkout_initial")
      window.App.Utils?.logCustomDatadogAction("stripe_checkout_initial_modal")
    }
  }, [visible])

  const isHolidayPromo = step === "holiday"

  if (isHolidayPromo && visible) window.App.Utils?.logCustomDatadogAction?.("holiday_checkout_modal_preview")

  return subscriptionPropsLoaded ? (
    <div className={`${styles["checkout-on-modal"]} checkout-on-modal`}>
      <Modal show={visible} includeCloseX={false}>
        <section className={styles["checkout-container"]}>
          {isHolidayPromo ? (
            <section className={`${styles["left"]} illustration-holiday`}>
              <div className={styles["inner"]} />
            </section>
          ) : (
            <section className={`${styles["left"]} illustration-${modalCopyObj.checkoutModalTarget}`}>
              <div className={styles["inner"]}>
                <h1 className={styles["ifttt"]}>
                  <img src={subscriptionProps["assets"].iftttWhiteSvgUrl} alt="IFTTT logo" width="122" height="37" />
                  {checkoutModalTarget === "pro" ? (
                    <img src={subscriptionProps["assets"].proPlusBadgeWhiteSvgUrl} alt="Pro+ badge" width="91" height="53" />
                  ) : (
                    <img src={subscriptionProps["assets"].proBadgeWhiteSvgUrl} alt="Pro badge" width="91" height="53" />
                  )}
                </h1>
                <div className={styles["cta-subheader"]}>
                  {modalCopyObj.benefitsHeader}
                </div>
                <ul>
                  {paidBenefits[checkoutModalTarget].map((benefit, index) => (
                    <li key={index}>{benefit}</li>
                  ))}
                </ul>
              </div>
            </section>
          )}
          <section className={styles["right"]}>
            <div className={styles["close"]} onClick={onClose}>
              <span className="close-x">{parse(closeX)}</span>
            </div>
            <header
              className={classnames(styles["onboarding-header"], "pb1 pb0--phone", {
                [styles["holiday-promo"]]: isHolidayPromo,
              })}
            >
              <h1 className={styles["ifttt"]}>
                <img src={subscriptionProps["assets"].iftttSvgUrl} alt="IFTTT logo" width="122" height="37" />
                {checkoutModalTarget === "pro" ? (
                  <img src={subscriptionProps["assets"].proPlusBadgeSvgUrl} alt="Pro+ badge" width="91" height="53" />
                ) : (
                  <img src={subscriptionProps["assets"].proBadgeSvgUrl} alt="Pro badge" width="91" height="53" />
                )}
              </h1>
              <h2 className={`${styles["modal-title"]}`}>
                {title}
              </h2>
              {window.App.holidayPromoReady && (
                <div className={"holiday-deal-content"}>
                  <span className={"holiday-discount-badge"}>LIMITED-TIME OFFER - 35% OFF</span>
                  <p className={"tech-the-halls"}>Tech the halls with 35% off an annual Pro plan.</p>
                </div>
              )}
              {eligibleForTrialCopy ? (
                <p className="subheader txt-body-3">
                  {eligibleForTrialCopy}
                </p>
              ) : null}
            </header>
            <PurchaseModal
              onClose={onClose}
              onUpgrade={() => onUpgrade(checkoutModalTarget)}
              subscriptionProps={{
                ...subscriptionProps,
                initial_promo_code: initialPromoCode,
              }}
              target={checkoutModalTarget}
            />
          </section>
        </section>
      </Modal>
    </div>
  ) : null
}

export default CheckoutModal
